import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { RidePhoto } from "../api/types";

const SWIPE_PX = 50;
const DISMISS_PX = 90;

/**
 * Full-screen viewer for one ride's photos. Mounted only while open, so it
 * takes its starting photo as a plain prop and owns the index from there.
 * Portalled to `document.body` so the sticky nav (zIndex 5/10 in Chrome) and
 * any page overflow can't clip it.
 */
export function PhotoLightbox({
  photos,
  startIndex,
  title,
  subtitle,
  onClose,
}: {
  photos: RidePhoto[];
  startIndex: number;
  title: string;
  subtitle?: string;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(() => Math.min(Math.max(startIndex, 0), photos.length - 1));
  const overlayRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const touch = useRef<{ x: number; y: number } | null>(null);

  const last = photos.length - 1;
  const go = useCallback(
    (delta: number) => setIndex((i) => Math.min(Math.max(i + delta, 0), last)),
    [last],
  );

  // Esc closes; arrows page through. Bound to the window so the keys work no
  // matter which control inside the overlay has focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "Home") setIndex(0);
      else if (e.key === "End") setIndex(last);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, last, onClose]);

  // Freeze the page behind the overlay, and hand focus back to whatever opened
  // it on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    overlayRef.current?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, []);

  // Warm the neighbours so paging doesn't flash an empty frame.
  useEffect(() => {
    for (const i of [index - 1, index + 1]) {
      const p = photos[i];
      if (p) new Image().src = p.url;
    }
  }, [index, photos]);

  // Keep the active thumbnail in view when paging with keys or arrows. Set
  // scrollLeft directly rather than scrollIntoView() — the latter also scrolls
  // the frozen page behind the overlay, which then shows through on close.
  useEffect(() => {
    const strip = stripRef.current;
    const thumb = strip?.querySelector<HTMLElement>(`[data-thumb="${index}"]`);
    if (!strip || !thumb) return;
    strip.scrollLeft = thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2;
  }, [index]);

  const photo = photos[index];
  if (!photo) return null;

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    touch.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touch.current;
    touch.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > Math.abs(dy)) {
      if (Math.abs(dx) > SWIPE_PX) go(dx < 0 ? 1 : -1);
    } else if (dy > DISMISS_PX) {
      onClose();
    }
  };

  return createPortal(
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Photos from ${title}`}
      tabIndex={-1}
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "#0c0a09",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        outline: "none",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "14px 16px",
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--display)",
              fontSize: 20,
              letterSpacing: -0.4,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: 12, opacity: 0.65, marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.7, fontVariantNumeric: "tabular-nums" }}>
            {index + 1} / {photos.length}
          </span>
          <button
            onClick={onClose}
            aria-label="Close photo viewer"
            style={{
              width: 40,
              height: 40,
              borderRadius: 999,
              border: "none",
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              fontSize: 20,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>
      </header>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 8px",
        }}
      >
        <img
          key={photo.url}
          src={photo.url}
          alt={photo.caption || `Photo ${index + 1} of ${photos.length} from ${title}`}
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 6 }}
        />
        {photos.length > 1 && (
          <>
            <NavArrow dir="prev" disabled={index === 0} onClick={() => go(-1)} />
            <NavArrow dir="next" disabled={index === last} onClick={() => go(1)} />
          </>
        )}
      </div>

      <footer style={{ flexShrink: 0, padding: "12px 16px 16px" }} onClick={(e) => e.stopPropagation()}>
        {photo.caption && (
          <p
            style={{
              margin: "0 auto 12px",
              maxWidth: 640,
              textAlign: "center",
              fontSize: 14,
              lineHeight: 1.4,
              opacity: 0.85,
            }}
          >
            {photo.caption}
          </p>
        )}
        {photos.length > 1 && (
          <div
            ref={stripRef}
            style={{
              position: "relative", // so thumb.offsetLeft is measured against the strip
              display: "flex",
              gap: 8,
              overflowX: "auto",
              justifyContent: "safe center",
              paddingBottom: 4,
            }}
          >
            {photos.map((p, i) => (
              <button
                key={i}
                data-thumb={i}
                onClick={() => setIndex(i)}
                aria-label={`Show photo ${i + 1}`}
                aria-current={i === index}
                style={{
                  flex: "0 0 auto",
                  width: 60,
                  height: 44,
                  padding: 0,
                  borderRadius: 6,
                  overflow: "hidden",
                  border: i === index ? "2px solid var(--accent)" : "2px solid transparent",
                  background: "none",
                  opacity: i === index ? 1 : 0.5,
                  transition: "opacity 120ms",
                }}
              >
                <img
                  src={p.url}
                  alt=""
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </button>
            ))}
          </div>
        )}
      </footer>
    </div>,
    document.body,
  );
}

function NavArrow({
  dir,
  disabled,
  onClick,
}: {
  dir: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      aria-label={dir === "prev" ? "Previous photo" : "Next photo"}
      style={{
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        [dir === "prev" ? "left" : "right"]: 8,
        width: 48,
        height: 48,
        borderRadius: 999,
        border: "none",
        background: "rgba(255,255,255,0.12)",
        color: "#fff",
        fontSize: 22,
        lineHeight: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: disabled ? 0.22 : 1,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {dir === "prev" ? "‹" : "›"}
    </button>
  );
}
